import {
  StoreOrderVariance as ApiStoreOrderVariance,
  StoreOrderVarianceListResponse as ApiStoreOrderVarianceListResponse,
  SupplierStoreOrderVariance as ApiSupplierStoreOrderVariance,
  SupplierStoreOrderVarianceListResponse as ApiSupplierStoreOrderVarianceListResponse,
  ListStoreOrderVariancesVarianceThresholdPercentEnum,
  ListSupplierStoreOrderVariancesMinimumOverdueDaysEnum,
  ListSupplierStoreOrderVariancesVarianceThresholdPercentEnum,
} from '@/gen/api';
import {
  MinimumOverdueDays,
  OrderVariance,
  OrderVarianceDirection,
  OrderVarianceKind,
  StoreOrderVariance,
  StoreOrderVariances,
  SupplierStoreOrderVariance,
  SupplierStoreOrderVariances,
  VarianceThresholdPercent,
} from '@/models/OrderVariance';

const parseOrderVarianceKind = (kind: string): OrderVarianceKind => {
  switch (kind) {
    case OrderVarianceKind.SkuCount:
      return OrderVarianceKind.SkuCount;
    case OrderVarianceKind.OrderValue:
      return OrderVarianceKind.OrderValue;
    default:
      return OrderVarianceKind.SkuCount;
  }
};

const parseOrderVarianceDirection = (direction: string): OrderVarianceDirection => {
  switch (direction) {
    case OrderVarianceDirection.BelowAverage:
      return OrderVarianceDirection.BelowAverage;
    case OrderVarianceDirection.AboveAverage:
      return OrderVarianceDirection.AboveAverage;
    default:
      return OrderVarianceDirection.BelowAverage;
  }
};

const toOrderVariance = (variance: ApiStoreOrderVariance['variances'][number]): OrderVariance => ({
  kind: parseOrderVarianceKind(variance.kind),
  direction: parseOrderVarianceDirection(variance.direction),
  actualValue: variance.actualValue,
  averageValue: variance.averageValue,
  variancePercent: variance.variancePercent,
  thresholdPercent: Number(variance.thresholdPercent),
  currencyCode: variance.currencyCode,
  sampleOrderCount: variance.sampleOrderCount,
});

export const toStoreOrderVariance = (variance: ApiStoreOrderVariance): StoreOrderVariance => ({
  orderId: variance.orderId,
  orderCode: variance.orderCode,
  supplierId: variance.supplierId,
  storeId: variance.storeId,
  variances: variance.variances.map(toOrderVariance),
});

export const toStoreOrderVariances = (
  response: ApiStoreOrderVarianceListResponse,
): StoreOrderVariances => ({
  items: response.items.map(toStoreOrderVariance),
  totalCount: response.totalCount,
  estimatedTotalCount: response.estimatedTotalCount,
});

export const toSupplierStoreOrderVariance = (
  variance: ApiSupplierStoreOrderVariance,
): SupplierStoreOrderVariance => ({
  supplierId: variance.supplierId,
  storeId: variance.storeId,
  supplierName: variance.supplierName,
  storeName: variance.storeName,
  kind: variance.kind,
  averageIntervalDays: variance.averageIntervalDays,
  daysSinceLastOrder: variance.daysSinceLastOrder,
  daysOverdue: variance.daysOverdue,
  thresholdDays: variance.thresholdDays,
  thresholdPercent: Number(variance.thresholdPercent),
  orderCount: variance.orderCount,
  lastOrderId: variance.lastOrderId,
  lastOrderCreatedAt: variance.lastOrderCreatedAt,
});

export const toSupplierStoreOrderVariances = (
  response: ApiSupplierStoreOrderVarianceListResponse,
): SupplierStoreOrderVariances => ({
  items: response.items.map(toSupplierStoreOrderVariance),
  totalCount: response.totalCount,
  estimatedTotalCount: response.estimatedTotalCount,
});

export const toApiStoreVarianceThresholdPercent = (
  threshold?: VarianceThresholdPercent,
): ListStoreOrderVariancesVarianceThresholdPercentEnum | undefined => {
  switch (threshold) {
    case '30':
      return ListStoreOrderVariancesVarianceThresholdPercentEnum._30;
    case '50':
      return ListStoreOrderVariancesVarianceThresholdPercentEnum._50;
    case '75':
      return ListStoreOrderVariancesVarianceThresholdPercentEnum._75;
    case '100':
      return ListStoreOrderVariancesVarianceThresholdPercentEnum._100;
    default:
      return undefined;
  }
};

export const toApiSupplierStoreVarianceThresholdPercent = (
  threshold?: VarianceThresholdPercent,
): ListSupplierStoreOrderVariancesVarianceThresholdPercentEnum | undefined => {
  switch (threshold) {
    case '30':
      return ListSupplierStoreOrderVariancesVarianceThresholdPercentEnum._30;
    case '50':
      return ListSupplierStoreOrderVariancesVarianceThresholdPercentEnum._50;
    case '75':
      return ListSupplierStoreOrderVariancesVarianceThresholdPercentEnum._75;
    case '100':
      return ListSupplierStoreOrderVariancesVarianceThresholdPercentEnum._100;
    default:
      return undefined;
  }
};

export const toApiMinimumOverdueDays = (
  minimumOverdueDays?: MinimumOverdueDays,
): ListSupplierStoreOrderVariancesMinimumOverdueDaysEnum | undefined => {
  switch (minimumOverdueDays) {
    case '0':
      return ListSupplierStoreOrderVariancesMinimumOverdueDaysEnum._0;
    case '2':
      return ListSupplierStoreOrderVariancesMinimumOverdueDaysEnum._2;
    case '3':
      return ListSupplierStoreOrderVariancesMinimumOverdueDaysEnum._3;
    case '7':
      return ListSupplierStoreOrderVariancesMinimumOverdueDaysEnum._7;
    case '14':
      return ListSupplierStoreOrderVariancesMinimumOverdueDaysEnum._14;
    default:
      return undefined;
  }
};
